/**
 * Work Type and Execution Recipe types.
 * Canon reference: Doc 21 (21-planning-work-type-and-execution-recipe-model.v1.md)
 * Schema reference: migration-work-type-recipes.sql
 *
 * INTEGRATION NOTE:
 * If this codebase already defines a WorkUnit interface in src/types/planning.ts,
 * add resolved_recipe_id to it rather than duplicating WorkUnit here.
 * Search for "WorkUnit" in src/types/ before using WorkUnitWithRecipe below.
 */

// ---------------------------------------------------------------------------
// Work Type (registry-controlled vocabulary)
// ---------------------------------------------------------------------------

/**
 * Built-in work type IDs. Organizations may define additional custom types
 * as string literals beyond this union — use `WorkType | string` where custom
 * types must be accepted, or `WorkType` where only built-ins are valid.
 */
export type WorkType =
  | 'novel_build'
  | 'bug_fix'
  | 'tech_debt'
  | 'safe_feature'
  | 'operational'
  | 'venture_bet'
  | 'known_replication';

/**
 * Approach kind — controls how the planning engine and UI present the work.
 */
export type ApproachKind =
  | 'experimental'
  | 'diagnostic'
  | 'iterative'
  | 'incremental'
  | 'runbook'
  | 'validating'
  | 'replicative';

// ---------------------------------------------------------------------------
// Process Artifact descriptor
// ---------------------------------------------------------------------------

/**
 * A single required process artifact for a recipe.
 * blocking: true → triggers RECIPE_RESOLVED readiness gate (WorkUnit cannot be scheduled).
 * blocking: false → recommended but non-blocking.
 */
export interface RecipeProcessArtifact {
  artifact_id: string;
  label: string;
  description: string;
  blocking: boolean;
}

// ---------------------------------------------------------------------------
// Governance Policy reference
// ---------------------------------------------------------------------------

export interface RecipeGovernancePolicyRef {
  policy_id: string;
  label: string;
  description: string;
}

// ---------------------------------------------------------------------------
// Resource allocation (demand template entry)
// ---------------------------------------------------------------------------

/**
 * One baseline demand line on a work type recipe. A deliberate subset of
 * LaunchProfilePhase (timePhasedPlanning.types.ts) so the two never diverge.
 * `workUnits` is UN-multiplied; the uncertainty multiplier is applied at
 * demand-math time, never baked into the stored value.
 */
export interface ResourceAllocation {
  /** Keys on the resource-type taxonomy (resourceTypesStore). */
  resourceType: string;
  /** Baseline work units per typical instance of this work type. */
  workUnits: number;
}

// ---------------------------------------------------------------------------
// ExecutionRecipeTemplate (maps to execution_recipe_templates table)
// ---------------------------------------------------------------------------

/**
 * A durable execution recipe template.
 * Built-in entries (is_builtin = true) are immutable.
 * Organizations may add custom entries.
 */
export interface ExecutionRecipeTemplate {
  id: string;
  work_type_ref: WorkType | string;
  name: string;
  approach_kind: ApproachKind;
  /** Multiplied against WorkUnit estimated_effort in constraint evaluation. Always >= 1.0. */
  uncertainty_multiplier: number;
  /** Artifacts that must exist before scheduling. blocking:true ones gate RECIPE_RESOLVED. */
  required_process_artifacts: RecipeProcessArtifact[];
  governance_policy_refs: RecipeGovernancePolicyRef[];
  /**
   * Baseline per-resource-type demand. Empty until the recipe is given a demand
   * template. Items inherit this via the explicit "estimate from work type" action.
   */
  resource_allocations: ResourceAllocation[];
  description: string | null;
  /** True for built-in entries — guarded against mutation at the DB level. */
  is_builtin: boolean;
  /** Null for built-in (global) recipes; set for workspace-custom work types. */
  workspace_id: string | null;
  /** When false, hidden from intake pickers but preserved for existing items. */
  active: boolean;
  /** Workspace-defined extension metadata. */
  metadata: Record<string, unknown>;
  /** Monotonic version within a (workspace_id, work_type_ref) supersession chain. */
  version_number: number;
  /** Self-FK to the row this version supersedes; null for the original. */
  supersedes_id: string | null;
  created_at: string;
  updated_at?: string;
}

// ---------------------------------------------------------------------------
// WorkUnit recipe state
// ---------------------------------------------------------------------------

/**
 * The state of recipe resolution for a WorkUnit.
 * - recipe_resolved: resolved_recipe_id is set; scheduling may proceed (subject to other gates).
 * - recipe_pending: resolved_recipe_id is null; RECIPE_RESOLVED blocker is active.
 */
export type WorkUnitRecipeState = 'recipe_resolved' | 'recipe_pending';

/**
 * Derives the WorkUnit's recipe state from its resolved_recipe_id.
 * Use this helper rather than checking null inline.
 */
export function getWorkUnitRecipeState(
  resolved_recipe_id: string | null | undefined
): WorkUnitRecipeState {
  return resolved_recipe_id ? 'recipe_resolved' : 'recipe_pending';
}

// ---------------------------------------------------------------------------
// Additive fields for WorkUnit (add to existing WorkUnit interface if one exists)
// ---------------------------------------------------------------------------

/**
 * Fields added to work_units by this feature.
 * If the codebase already has a WorkUnit interface, merge these fields into it
 * rather than using this standalone type.
 */
export interface WorkUnitRecipeFields {
  /**
   * FK → execution_recipe_templates.id
   * Null when item.work_type is unset → recipe_pending state → cannot schedule.
   * Immutable once set for a given PlanVersion.
   */
  resolved_recipe_id: string | null;
}

// ---------------------------------------------------------------------------
// Additive field for Item (add to existing Item interface if one exists)
// ---------------------------------------------------------------------------

/**
 * Field added to items by this feature.
 * If the codebase already has an Item interface, add work_type to it
 * rather than using this standalone type.
 */
export interface ItemWorkTypeField {
  /**
   * Registry-controlled work type classification.
   * Set during item creation or enrichment.
   * Read-only once the item enters Planning.
   */
  work_type: WorkType | string | null;
}

// ---------------------------------------------------------------------------
// Constraint evaluation integration
// ---------------------------------------------------------------------------

/**
 * Computes the uncertainty-adjusted effort for a WorkUnit.
 * This value is used as the demand input for capacity feasibility checks (Doc 13, Step 4).
 *
 * @param baseEffort The WorkUnit's base estimated effort (in whatever unit the system uses)
 * @param recipe The resolved recipe template (or null if recipe_pending)
 * @returns The adjusted effort. Returns baseEffort unchanged if no recipe is resolved,
 *          but callers should surface RECIPE_RESOLVED blocker in that case rather than
 *          silently using the unadjusted estimate.
 */
export function computeAdjustedEffort(
  baseEffort: number,
  recipe: ExecutionRecipeTemplate | null
): number {
  if (!recipe) return baseEffort;
  return baseEffort * recipe.uncertainty_multiplier;
}

/**
 * Returns the blocking artifacts from a recipe — the ones that must exist
 * before the WorkUnit can be scheduled (RECIPE_RESOLVED gate).
 */
export function getBlockingArtifacts(recipe: ExecutionRecipeTemplate): RecipeProcessArtifact[] {
  return recipe.required_process_artifacts.filter((a) => a.blocking);
}

// ---------------------------------------------------------------------------
// Demand template math
// ---------------------------------------------------------------------------

export interface RecipeResourceDemand {
  /** Per-resource-type work units (multiplier applied when requested). */
  byResourceWorkUnits: Record<string, number>;
  /** Per-resource-type hours = workUnits × defaultWuHours. */
  byResourceHours: Record<string, number>;
  /** Sum across all resource types. */
  totalWorkUnits: number;
  totalHours: number;
}

/**
 * Resolves a recipe's demand template into concrete per-resource demand.
 *
 * The uncertainty multiplier is applied HERE (once), matching where the engine
 * applies it today — never baked into stored allocations. Pass
 * `applyMultiplier: false` to see raw baselines (e.g. in the settings editor).
 */
export function computeRecipeResourceDemand(
  recipe: Pick<ExecutionRecipeTemplate, 'resource_allocations' | 'uncertainty_multiplier'>,
  defaultWuHours: number,
  applyMultiplier = true,
): RecipeResourceDemand {
  const multiplier = applyMultiplier
    ? recipe.uncertainty_multiplier >= 1
      ? recipe.uncertainty_multiplier
      : 1
    : 1;
  const byResourceWorkUnits: Record<string, number> = {};
  const byResourceHours: Record<string, number> = {};
  let totalWorkUnits = 0;
  let totalHours = 0;

  for (const alloc of recipe.resource_allocations ?? []) {
    if (!alloc?.resourceType || !(alloc.workUnits > 0)) continue;
    const wu = alloc.workUnits * multiplier;
    const hours = wu * defaultWuHours;
    byResourceWorkUnits[alloc.resourceType] = (byResourceWorkUnits[alloc.resourceType] ?? 0) + wu;
    byResourceHours[alloc.resourceType] = (byResourceHours[alloc.resourceType] ?? 0) + hours;
    totalWorkUnits += wu;
    totalHours += hours;
  }

  return { byResourceWorkUnits, byResourceHours, totalWorkUnits, totalHours };
}

/**
 * Validates a proposed allocation list against the enabled resource-type
 * taxonomy. Returns an error string, or null when valid.
 */
export function validateResourceAllocations(
  allocations: ResourceAllocation[],
  enabledResourceTypeKeys: string[],
): string | null {
  const seen = new Set<string>();
  for (const a of allocations) {
    if (!a.resourceType) return 'Every allocation needs a resource type.';
    if (!enabledResourceTypeKeys.includes(a.resourceType)) {
      return `Resource type "${a.resourceType}" is not enabled in this workspace.`;
    }
    if (seen.has(a.resourceType)) return `Resource type "${a.resourceType}" is listed twice.`;
    seen.add(a.resourceType);
    if (!(a.workUnits > 0)) return 'Work units must be greater than zero.';
  }
  return null;
}

/**
 * Returns the recommended (non-blocking) artifacts from a recipe.
 */
export function getRecommendedArtifacts(recipe: ExecutionRecipeTemplate): RecipeProcessArtifact[] {
  return recipe.required_process_artifacts.filter((a) => !a.blocking);
}

// ---------------------------------------------------------------------------
// Readiness gate constant
// ---------------------------------------------------------------------------

/**
 * The readiness gate code for unresolved recipe.
 * Must match the entry in registry-codes-and-blockers.yaml.
 */
export const RECIPE_RESOLVED_GATE_CODE = 'RECIPE_RESOLVED' as const;

/**
 * The planning audit event type emitted when a recipe is resolved.
 */
export const RECIPE_RESOLVED_AUDIT_EVENT = 'recipe_resolved' as const;

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** Human-readable labels for approach kinds. */
export const APPROACH_KIND_LABELS: Record<ApproachKind, string> = {
  experimental: 'Experimental',
  diagnostic: 'Diagnostic',
  iterative: 'Iterative',
  incremental: 'Incremental',
  runbook: 'Runbook-Driven',
  validating: 'Validation-Driven',
  replicative: 'Replicative',
};

/** Human-readable labels for built-in work types. */
export const WORK_TYPE_LABELS: Record<WorkType, string> = {
  novel_build: 'Novel Build',
  bug_fix: 'Bug Fix',
  tech_debt: 'Technical Debt Retirement',
  safe_feature: 'Safe / Incremental Feature',
  operational: 'Operational Work',
  venture_bet: 'Venture Bet',
  known_replication: 'Known Solution Replication',
};

/** Built-in work type options for selectors (Canon Doc 21 §2.2). */
export const BUILTIN_WORK_TYPE_OPTIONS: Array<{
  value: WorkType;
  approachKind: ApproachKind;
  multiplier: number;
}> = [
  { value: 'novel_build', approachKind: 'experimental', multiplier: 1.5 },
  { value: 'bug_fix', approachKind: 'diagnostic', multiplier: 1.1 },
  { value: 'tech_debt', approachKind: 'iterative', multiplier: 1.2 },
  { value: 'safe_feature', approachKind: 'incremental', multiplier: 1.0 },
  { value: 'operational', approachKind: 'runbook', multiplier: 1.3 },
  { value: 'venture_bet', approachKind: 'validating', multiplier: 2.0 },
  { value: 'known_replication', approachKind: 'replicative', multiplier: 1.0 },
];

/** Built-in work type IDs, in registry order (Canon Doc 21 §2.2). */
export const BUILTIN_WORK_TYPE_IDS: WorkType[] = BUILTIN_WORK_TYPE_OPTIONS.map((o) => o.value);

/**
 * Hint shown on import surfaces that accept a raw `work_type` slug.
 *
 * Derived from the registry rather than hand-written. The previous hardcoded
 * examples suggested `feature`, `bug`, `research`, `infrastructure`, `initiative`
 * and `spike` — none of which is a valid work type ID. `items.work_type` has no
 * FK (custom types are allowed), so invalid values imported this way persisted
 * silently and resolved to no recipe, leaving the work unit permanently
 * recipe_pending.
 */
export const WORK_TYPE_IMPORT_HINT =
  `Work type slug — one of: ${BUILTIN_WORK_TYPE_IDS.join(' | ')}, or a workspace custom type.`;

export const APPROACH_KIND_OPTIONS: ApproachKind[] = [
  'experimental',
  'diagnostic',
  'iterative',
  'incremental',
  'runbook',
  'validating',
  'replicative',
];

export interface WorkTypeOption {
  value: string;
  label: string;
  approachKind: ApproachKind;
  multiplier: number;
  isBuiltin: boolean;
  description?: string | null;
  active: boolean;
}

/** Merge built-in registry entries with workspace-custom recipes for pickers. */
export function recipesToWorkTypeOptions(
  recipes: ExecutionRecipeTemplate[],
  workspaceId?: string | null,
): WorkTypeOption[] {
  const builtins = BUILTIN_WORK_TYPE_OPTIONS.map((b) => ({
    value: b.value,
    label: WORK_TYPE_LABELS[b.value],
    approachKind: b.approachKind,
    multiplier: b.multiplier,
    isBuiltin: true,
    description: recipes.find((r) => r.work_type_ref === b.value)?.description ?? null,
    active: true,
  }));

  if (!workspaceId) return builtins;

  const customs = recipes
    .filter((r) => !r.is_builtin && r.workspace_id === workspaceId && r.active)
    .map((r) => ({
      value: r.work_type_ref,
      label: r.name,
      approachKind: r.approach_kind,
      multiplier: r.uncertainty_multiplier,
      isBuiltin: false,
      description: r.description,
      active: r.active,
    }));

  // A workspace recipe for a work type REPLACES the built-in of the same
  // work_type_ref — it does not sit beside it. Concatenating produced two
  // entries with the same value: a picker offering fourteen options for seven
  // work types, "Bug Fix" once plain and once marked custom, with nothing to
  // tell the reader which one the plan would actually use. resolveRecipeIdForWorkType
  // already answers that ("workspace row wins over the global built-in"); this
  // is the same rule on the choosing side.
  const byValue = new Map<string, WorkTypeOption>();
  for (const opt of builtins) byValue.set(opt.value, opt);
  for (const opt of customs) byValue.set(opt.value, opt);
  return [...byValue.values()];
}

export function workTypeDisplayLabel(
  workType: string | null | undefined,
  recipes: ExecutionRecipeTemplate[],
): string {
  if (!workType) return '';
  const builtin = WORK_TYPE_LABELS[workType as WorkType];
  if (builtin) return builtin;
  return recipes.find((r) => r.work_type_ref === workType)?.name ?? workType;
}
